import React, { useState } from 'react'
import { ShoppingBag, X, Trash2, Plus, Minus, ChevronUp } from 'lucide-react'
import { useCart } from '../../contexts/CartContext'
import { formatPrice } from '../../lib/square'
import { useNavigate } from 'react-router-dom'

interface FloatingCartProps {
  eventId: string
  canPlaceOrders: boolean
}

export const FloatingCart: React.FC<FloatingCartProps> = ({ eventId, canPlaceOrders }) => {
  const { items, totalItems, subtotal, taxRate, taxAmount, totalAmount, updateQuantity, removeItem, clearCart } = useCart()
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()

  if (totalItems === 0 || !canPlaceOrders) {
    return null
  }

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      clearCart()
      setIsExpanded(false)
    }
  }

  const handleCheckout = () => {
    navigate('/checkout')
  }

  const handleQuantityChange = (menuItemId: string, newQuantity: number) => {
    if (newQuantity === 0) {
      if (window.confirm('Remove this item from your cart?')) {
        removeItem(menuItemId)
      }
    } else {
      updateQuantity(menuItemId, newQuantity)
    }
  }

  return (
    <>
      {/* Desktop Floating Cart */}
      <div className="hidden md:block">
        <div className={`fixed bottom-6 right-6 z-50 bg-card text-card-foreground rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm transition-all duration-300 ${
          isExpanded ? 'w-96' : 'w-80'
        }`}>
          {!isExpanded ? (
            /* Collapsed State */
            <div
              className="p-6 cursor-pointer hover:shadow-3xl transition-shadow"
              onClick={() => setIsExpanded(true)}
            >
              <div className="flex items-center space-x-3 mb-5">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-xl">
                  <ShoppingBag color="#7f1d1d" size={22} className="dark:text-secret-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Your Order</h3>
                  <p className="text-xs text-muted-foreground">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
                </div>
                <ChevronUp className="ml-auto text-muted-foreground" size={20} />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold text-foreground">{formatPrice(subtotal * 100)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span>
                  <span className="font-semibold text-foreground">{formatPrice(taxAmount * 100)}</span>
                </div>
                <div className="flex justify-between items-center text-sm border-t border-gray-200 dark:border-gray-700 pt-2">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-bold text-foreground">{formatPrice(totalAmount * 100)}</span>
                </div>

                <button
                  type="button"
                  className="w-full secret-btn-primary py-3 rounded-xl font-semibold text-white"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCheckout()
                  }}
                >
                  Checkout
                </button>

                <p className="text-xs text-center text-muted-foreground">
                  Click to view items
                </p>
              </div>
            </div>
          ) : (
            /* Expanded State */
            <div className="max-h-[600px] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <ShoppingBag color="#7f1d1d" size={20} className="dark:text-secret-primary" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Your Order</h3>
                  <span className="text-xs text-muted-foreground">({totalItems} item{totalItems !== 1 ? 's' : ''})</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className="p-1 hover:bg-muted rounded-full transition-colors"
                >
                  <X size={20} className="text-muted-foreground" />
                </button>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
                {items.map((item) => (
                  <div key={item.menuItem.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-foreground">{item.menuItem.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatPrice(item.menuItem.price * 100)} each
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(item.menuItem.id, item.quantity - 1)}
                        className="w-7 h-7 rounded-full bg-secret-primary hover:bg-secret-primary-dark transition-colors flex items-center justify-center group"
                      >
                        <Minus size={14} className="text-white" />
                      </button>
                      <span className="text-sm font-medium min-w-[1.5rem] text-center text-gray-900 dark:text-gray-100">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(item.menuItem.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full bg-secret-primary hover:bg-secret-primary-dark transition-colors flex items-center justify-center group"
                      >
                        <Plus size={14} className="text-white" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(item.menuItem.id)}
                        className="w-7 h-7 rounded-full bg-secret-primary hover:bg-red-500 transition-colors flex items-center justify-center ml-1 group"
                      >
                        <Trash2 size={14} className="text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold text-foreground">{formatPrice(subtotal * 100)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span>
                  <span className="font-semibold text-foreground">{formatPrice(taxAmount * 100)}</span>
                </div>
                <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-2">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="text-lg font-bold text-foreground">{formatPrice(totalAmount * 100)}</span>
                </div>

                <button
                  type="button"
                  onClick={handleCheckout}
                  className="w-full secret-btn-primary py-3 rounded-xl font-semibold text-white"
                >
                  Proceed to Checkout
                </button>

                <button
                  type="button"
                  onClick={handleClearCart}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-red-500 transition-colors"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Floating Cart */}
      <div className="md:hidden">
        <div className={`fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md text-card-foreground border-t-2 border-gray-200/50 dark:border-gray-700/50 transition-all duration-300 pb-safe ${
          isExpanded ? 'h-[80dvh]' : 'h-auto'
        }`}
        style={{ paddingBottom: isExpanded ? undefined : `max(env(safe-area-inset-bottom, 0px), 0px)` }}
        >
          {!isExpanded ? (
            /* Collapsed State Mobile */
            <div
              className="p-4 cursor-pointer"
              onClick={() => setIsExpanded(true)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-red-100 dark:bg-red-900/20 rounded-lg">
                    <ShoppingBag color="#7f1d1d" size={18} className="dark:text-secret-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Your Order</h3>
                    <p className="text-xs text-muted-foreground">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <ChevronUp className="text-muted-foreground" size={20} />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold text-foreground">{formatPrice(subtotal * 100)}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span>
                  <span className="font-semibold text-foreground">{formatPrice(taxAmount * 100)}</span>
                </div>
                <div className="flex justify-between items-center text-xs border-t border-gray-200 dark:border-gray-700 pt-2">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-bold text-foreground">{formatPrice(totalAmount * 100)}</span>
                </div>
                <button
                  type="button"
                  className="w-full secret-btn-primary px-6 py-2 rounded-lg font-semibold text-sm text-white mt-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCheckout()
                  }}
                >
                  Checkout
                </button>
              </div>
            </div>
          ) : (
            /* Expanded State Mobile */
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <ShoppingBag color="#7f1d1d" size={20} className="dark:text-secret-primary" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Your Order</h3>
                  <span className="text-xs text-muted-foreground">({totalItems})</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className="p-1 hover:bg-muted rounded-full transition-colors"
                >
                  <X size={20} className="text-muted-foreground" />
                </button>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {items.map((item) => (
                  <div key={item.menuItem.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-foreground">{item.menuItem.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatPrice(item.menuItem.price * 100)} each
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(item.menuItem.id, item.quantity - 1)}
                        className="w-8 h-8 rounded-full bg-secret-primary hover:bg-secret-primary-dark transition-colors flex items-center justify-center group"
                      >
                        <Minus size={16} className="text-white" />
                      </button>
                      <span className="text-sm font-medium min-w-[1.5rem] text-center text-gray-900 dark:text-gray-100">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(item.menuItem.id, item.quantity + 1)}
                        className="w-8 h-8 rounded-full bg-secret-primary hover:bg-secret-primary-dark transition-colors flex items-center justify-center group"
                      >
                        <Plus size={16} className="text-white" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(item.menuItem.id)}
                        className="w-8 h-8 rounded-full bg-secret-primary hover:bg-red-500 transition-colors flex items-center justify-center ml-1 group"
                      >
                        <Trash2 size={16} className="text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold text-foreground">{formatPrice(subtotal * 100)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span>
                  <span className="font-semibold text-foreground">{formatPrice(taxAmount * 100)}</span>
                </div>
                <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-2">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="text-lg font-bold text-foreground">{formatPrice(totalAmount * 100)}</span>
                </div>

                <button
                  type="button"
                  onClick={handleCheckout}
                  className="w-full secret-btn-primary py-3 rounded-xl font-semibold text-white"
                >
                  Proceed to Checkout
                </button>

                <button
                  type="button"
                  onClick={handleClearCart}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-red-500 transition-colors"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
